import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import { TaskCreatedToast } from '@components/TaskList/TaskCreatedToast.web';

/**
 * Shows the "Member Assigned" / "{taskName} has been assigned" toast used
 * after assigning members to a task. Web reuses the task-list custom toast;
 * native uses the plain toast like the other native member-management toasts
 * (message only, since the plain toast has a single line). Both go through the app-level
 * ToastProvider, so the toast survives the caller unmounting/popping.
 */
export function useMemberAssignedToast() {
  const toast = useToast();
  const { t } = useTranslation('models');

  return useCallback(
    (taskName: string) => {
      const title = t('nudge.memberAssigned.title');
      const message = t('nudge.memberAssigned.message', { taskName });

      if (Platform.OS !== 'web') {
        toast.show(message, { placement: 'bottom', duration: 3000 });
        return;
      }

      const toastId = toast.show(
        <TaskCreatedToast
          title={title}
          message={message}
          duration={3000}
          minWidth={388}
          onPress={() => {
            // Close toast immediately after click to avoid lingering UI.
            setTimeout(() => toast.hide(toastId), 0);
          }}
        />,
        {
          type: 'custom',
          duration: 3000,
          placement: 'bottom',
          animationType: 'slide-in',
          style: {
            marginBottom: 39,
            backgroundColor: 'transparent',
            shadowColor: 'transparent',
          },
        }
      );
    },
    [toast, t]
  );
}
